import { clsx } from 'clsx'
import { ChevronDown, ChevronLeft, ChevronRight, ChevronsUpDown, ChevronUp } from 'lucide-react'
import { type ReactNode, useState } from 'react'

interface Column<T> {
  key: string
  label: string
  sortable?: boolean
  render: (row: T) => ReactNode
}

type SortDirection = 'asc' | 'desc'

interface SortState {
  key: string
  direction: SortDirection
}

interface PaginationState {
  page: number
  perPage: number
  total: number
}

interface DataTableProps<T> {
  columns: Column<T>[]
  data: T[]
  rowKey?: (row: T, index: number) => string | number
  pagination?: PaginationState
  onPageChange?: (page: number) => void
  onSort?: (key: string, direction: SortDirection) => void
  onRowClick?: (row: T) => void
  isLoading?: boolean
  emptyMessage?: string
}

function SortIcon({ direction }: { direction: SortDirection | undefined }): React.JSX.Element {
  if (direction === 'asc') return <ChevronUp className="h-4 w-4" />
  if (direction === 'desc') return <ChevronDown className="h-4 w-4" />
  return <ChevronsUpDown className="h-4 w-4 text-text-tertiary" />
}

function defaultRowKey<T>(row: T, index: number): string | number {
  const r = row as Record<string, unknown>
  if (typeof r.id === 'string' || typeof r.id === 'number') return r.id
  return index
}

export function DataTable<T>({
  columns,
  data,
  rowKey = defaultRowKey,
  pagination,
  onPageChange,
  onSort,
  onRowClick,
  isLoading = false,
  emptyMessage = 'No data found',
}: DataTableProps<T>): React.JSX.Element {
  const [sort, setSort] = useState<SortState | null>(null)

  function handleSort(key: string): void {
    const newDirection: SortDirection =
      sort?.key === key && sort.direction === 'asc' ? 'desc' : 'asc'
    setSort({ key, direction: newDirection })
    onSort?.(key, newDirection)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center rounded-xl border border-surface-3 bg-surface-0 py-16">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center rounded-xl border border-surface-3 bg-surface-0 py-16">
        <p className="text-text-secondary">{emptyMessage}</p>
      </div>
    )
  }

  const totalPages = pagination ? Math.ceil(pagination.total / pagination.perPage) : 1
  const showStart = pagination ? (pagination.page - 1) * pagination.perPage + 1 : 1
  const showEnd = pagination
    ? Math.min(pagination.page * pagination.perPage, pagination.total)
    : data.length

  return (
    <div className="overflow-hidden rounded-xl border border-surface-3 bg-surface-0">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-surface-3 bg-surface-1">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={clsx(
                    'px-4 py-3 text-xs font-medium uppercase tracking-wider text-text-secondary',
                    col.sortable && 'cursor-pointer select-none hover:text-text-primary',
                  )}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {col.sortable && (
                      <SortIcon direction={sort?.key === col.key ? sort.direction : undefined} />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-2">
            {data.map((row, idx) => (
              <tr
                key={rowKey(row, idx)}
                className={clsx(
                  'transition-colors hover:bg-surface-1',
                  onRowClick && 'cursor-pointer',
                )}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((col) => (
                  <td key={col.key} className="whitespace-nowrap px-4 py-3 text-text-primary">
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination && (
        <div className="flex items-center justify-between border-t border-surface-3 px-4 py-3">
          <p className="text-sm text-text-secondary">
            Showing {showStart}-{showEnd} of {pagination.total}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-lg p-1.5 text-text-secondary hover:bg-surface-2 disabled:opacity-40"
              disabled={pagination.page <= 1}
              onClick={() => onPageChange?.(pagination.page - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm text-text-secondary">
              Page {pagination.page} of {totalPages}
            </span>
            <button
              type="button"
              className="rounded-lg p-1.5 text-text-secondary hover:bg-surface-2 disabled:opacity-40"
              disabled={pagination.page >= totalPages}
              onClick={() => onPageChange?.(pagination.page + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
